import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, EMPTY } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent, ButtonType } from '../../../../../shared/components/button/button.component';
import { TypeaheadSearchComponent } from '../../../../../shared/components/typeahead-search/typeahead-search.component';
import { Option } from '../../../../../shared/types/option';
import { CityService } from '../../../service/city.service';
import { CityData } from '../../../types/city-data';
import { Property } from '../../../types/property';

export interface PropertyDetailsValue {
    name: string;
    cityData: CityData | null;
    rooms: number;
}

@Component({
    selector: 'juyo-property-details-step',
    styleUrl: './property-details-step.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ButtonComponent, TypeaheadSearchComponent],
    template: `
        <div class="step-body">
            <h3 class="step-title">Property details</h3>
            <p class="step-subtitle">
                Tell us about this hotel. Timezone and currency are auto-detected from the city.
            </p>

            <label class="field-label" for="property-name">PROPERTY NAME <span class="required">*</span></label>
            <input
                id="property-name"
                type="text"
                class="field-input"
                placeholder="e.g. My Hotel"
                [ngModel]="propertyName()"
                (ngModelChange)="onPropertyNameChange($event)"
            />

            <div class="field-row">
                <div class="field-col">
                    <label class="field-label">CITY <span class="required">*</span></label>
                    <juyo-typeahead-search
                        [placeholder]="cityPlaceholder()"
                        [options]="cityOptions()"
                        (search)="onCitySearch($event)"
                        (optionClick)="onCitySelected($event)"
                    />
                </div>
                <div class="field-col">
                    <label class="field-label" for="property-rooms">
                        ROOM CAPACITY <span class="required">*</span></label>
                    <input
                        id="property-rooms"
                        type="number"
                        class="field-input"
                        min="1"
                        placeholder="e.g. 150"
                        [ngModel]="rooms()"
                        (ngModelChange)="onRoomsChange($event)"
                    />
                </div>
            </div>
        </div>

        <div class="step-footer">
            <div></div>
            <div class="footer-actions">
                @if (!createdProperty()) {
                    <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="cancel.emit()"> Cancel </juyo-button>
                }
                <juyo-button [disabled]="!canContinue() || saving()" (clicked)="onContinue()">
                    Continue <span class="material-symbols-outlined">arrow_right_alt</span>
                </juyo-button>
            </div>
        </div>
    `,
})
export class PropertyDetailsStepComponent {
    public saving = input<boolean>(false);
    public createdProperty = input<Property | null>(null);

    public continue = output<PropertyDetailsValue>();
    public cancel = output<void>();

    protected readonly ButtonType = ButtonType;
    private cityService = inject(CityService);
    private destroyRef = inject(DestroyRef);

    protected propertyName = signal('');
    protected selectedCity = signal<CityData | null>(null);
    protected cityOptions = signal<Option[]>([]);
    private lastCityResults = signal<CityData[]>([]);
    protected rooms = signal<number | null>(null);

    protected cityPlaceholder = computed(() => {
        const p = this.createdProperty();
        return p ? `${p.city}, ${p.countryCode}` : 'Start typing a city...';
    });

    private citySearch$ = new Subject<string>();

    constructor() {
        effect(() => {
            const property = this.createdProperty();
            if (property) {
                this.propertyName.set(property.name);
                this.rooms.set(property.rooms);
            }
        });

        this.citySearch$
            .pipe(
                debounceTime(300),
                switchMap((term) =>
                    this.cityService.getCities(term).pipe(
                        catchError(() => {
                            this.cityOptions.set([]);
                            return EMPTY;
                        }),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((cities) => {
                this.lastCityResults.set(cities);
                this.cityOptions.set(cities.map((c, i) => new Option(String(i), `${c.city}, ${c.countryCode}`)));
            });
    }

    protected canContinue = computed(
        () => !!this.propertyName().trim() &&
              (this.createdProperty() !== null || this.selectedCity() !== null) &&
              (this.rooms() ?? 0) > 0,
    );

    protected onCitySearch(term: string): void {
        this.selectedCity.set(null);
        if (term.length >= 2) {
            this.citySearch$.next(term);
        } else {
            this.cityOptions.set([]);
        }
    }

    protected onCitySelected(option: Option): void {
        const city = this.lastCityResults()[Number(option.key)];
        if (city) {
            this.selectedCity.set(city);
        }
        this.cityOptions.set([]);
    }

    protected onPropertyNameChange(name: string): void {
        this.propertyName.set(name)
    }

    protected onRoomsChange(rooms: number): void {
        this.rooms.set(rooms === null ? null : +rooms);
    }

    protected onContinue(): void {
        const rooms = this.rooms();
        if (!rooms) return;
        const created = this.createdProperty();
        let city = this.selectedCity();
        if (!city && created) {
            city = {
                city: created.city,
                countryCode: created.countryCode,
                currencyCode: created.currency,
                currencySymbol: '',
                timezone: created.timezone,
            };
        }
        if (!city) return;
        this.continue.emit({ name: this.propertyName(), cityData: city, rooms });
    }
}
